/**
 * Sprite-sheet VFX: camera-facing animated billboards for fire, smoke and
 * muzzle flashes.
 *
 * WHY ADDITIVE, AND WHY THAT IS NOT OPTIONAL HERE
 * The supplied fire and smoke sheets have NO usable alpha — both are opaque
 * RGB with a near-black background (measured from the decoded bytes: corner
 * pixels are (0,0,0) for fire and (1,1,1) for smoke; brightest smoke pixel is
 * (171,171,171) grey). Under `AdditiveBlending` a black background adds
 * nothing and disappears for free, which is the standard way this class of
 * asset is authored and exactly why it ships without alpha. Rendering them
 * with normal blending would draw a black card around every flame.
 *
 * The muzzle flashes are the opposite case — real pixel-art alpha, verified
 * lossless through the conversion — but they still composite additively
 * because a muzzle flash is light.
 *
 * BILLBOARDING is `THREE.Sprite`, not a manually-yawed quad like
 * `grass-mesh.ts` uses. Grass billboards on Y only (it is rooted in the
 * ground and must not lie down as the camera tilts); fire and smoke read
 * better as full screen-facing cards, which is what Sprite gives for free.
 */
import * as THREE from 'three';
import type { SpriteSpawn } from '../types';

export interface SpriteSheetDef {
  url: string;
  /** Grid layout. Frames run row-major from the TOP-LEFT tile. */
  cols: number;
  rows: number;
  /** Frames actually used — may be fewer than `cols * rows`. */
  frames: number;
  /** Playback rate. */
  fps: number;
}

/**
 * Measured, not assumed — every tile of both fire sheets and the packed smoke
 * sheet was cropped and compared against its source frame at RMSE 0, and the
 * smoke rate was read from the GIF's raw frame-delay bytes rather than an
 * averaged container value. See `public/sprites/MANIFEST.md`.
 *
 * The 5x5 fire sheet is used rather than the 25x1 strip that ships beside it:
 * the strip is 3200 px wide, past the 2048 px `MAX_TEXTURE_SIZE` that WebGL
 * only *guarantees*, so it would fail outright on a conservative GPU. Both
 * hold the same 25 frames and the same 409,600 pixels.
 */
export const SPRITE_SHEETS = {
  fire: { url: '/sprites/fire/fire-5x5.webp', cols: 5, rows: 5, frames: 25, fps: 24 },
  smoke: { url: '/sprites/smoke/smoke-4x4.webp', cols: 4, rows: 4, frames: 16, fps: 10 },
} as const satisfies Record<string, SpriteSheetDef>;

/**
 * Muzzle flashes, as individual textures rather than a sheet — the pack's own
 * readme recommends showing ONE random flash per shot, which is a pick from a
 * set, not an animation.
 */
export const MUZZLE_FLASHES: readonly string[] = [
  '/sprites/muzzle/muzzle-mega-1.webp',
  '/sprites/muzzle/muzzle-mega-2.webp',
  '/sprites/muzzle/muzzle-mega-3.webp',
  '/sprites/muzzle/muzzle-big-1.webp',
  '/sprites/muzzle/muzzle-big-2.webp',
  '/sprites/muzzle/muzzle-big-3.webp',
];

/** How long one flash stays visible. A real muzzle flash is ~1 frame. */
export const MUZZLE_FLASH_SECONDS = 0.055;

/**
 * UV offset for a frame index, given a grid.
 *
 * Pure and exported so the frame walk is unit-testable: a sheet played with
 * the rows inverted still animates, just garbled, and that is the kind of
 * thing nobody catches by glancing at a fire.
 *
 * glTF/Three textures address V from the BOTTOM, while sheets are laid out
 * from the top-left. Row 0 therefore maps to the TOP strip, i.e. the highest
 * V offset — which is the inversion this function exists to get right.
 */
export function frameOffset(index: number, cols: number, rows: number): { x: number; y: number } {
  const i = ((index % (cols * rows)) + cols * rows) % (cols * rows);
  const col = i % cols;
  const row = Math.floor(i / cols);
  return { x: col / cols, y: 1 - (row + 1) / rows };
}

const textureLoader = new THREE.TextureLoader();
/** One decode per URL; each sprite gets a `.clone()` so it can own its offset. */
const sheetCache = new Map<string, THREE.Texture>();

function loadSheet(url: string): THREE.Texture {
  let tex = sheetCache.get(url);
  if (!tex) {
    tex = textureLoader.load(url);
    tex.colorSpace = THREE.SRGBColorSpace;
    sheetCache.set(url, tex);
  }
  // Clone so `offset`/`repeat` are per-instance. The clone shares the decoded
  // image, so this costs no extra GPU upload for the pixels themselves.
  const own = tex.clone();
  own.needsUpdate = true;
  // NearestFilter would keep pixel-art crisp, but these are organic fire and
  // smoke; linear is right. CLAMP so a tile cannot bleed into its neighbour
  // at the edges once `repeat` is a fraction.
  own.wrapS = THREE.ClampToEdgeWrapping;
  own.wrapT = THREE.ClampToEdgeWrapping;
  return own;
}

export interface AnimatedSprite {
  object: THREE.Sprite;
  /** Advance playback. Loops. */
  update(dt: number): void;
  dispose(): void;
}

export interface SpriteOptions {
  /** World size of the billboard, metres. */
  width: number;
  height: number;
  /** 0-1 overall strength. */
  opacity?: number;
  /** Multiplied into the sheet's colour — warms or cools the effect. */
  color?: number;
  /**
   * Seconds to skip into the loop at construction. Two fires started on the
   * same frame otherwise flicker in perfect lockstep, which instantly reads
   * as fake — the same reasoning `phaseFromId` applies to NPC idles.
   */
  phase?: number;
}

export function createAnimatedSprite(def: SpriteSheetDef, opts: SpriteOptions): AnimatedSprite {
  const texture = loadSheet(def.url);
  texture.repeat.set(1 / def.cols, 1 / def.rows);

  const material = new THREE.SpriteMaterial({
    map: texture,
    transparent: true,
    // See this file's header: the sheets are light-on-black with no alpha, so
    // additive is what removes the background.
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    opacity: opts.opacity ?? 1,
    color: opts.color ?? 0xffffff,
    // Fog would tint an additive flame toward the fog colour and wash it out.
    fog: false,
  });

  const sprite = new THREE.Sprite(material);
  sprite.scale.set(opts.width, opts.height, 1);

  let t = opts.phase ?? 0;
  const apply = (): void => {
    const frame = Math.floor(t * def.fps) % def.frames;
    const o = frameOffset(frame, def.cols, def.rows);
    texture.offset.set(o.x, o.y);
  };
  apply();

  return {
    object: sprite,
    update(dt) {
      t += dt;
      apply();
    },
    dispose() {
      texture.dispose();
      material.dispose();
    },
  };
}

/**
 * A pool of one-shot muzzle flashes.
 *
 * Pooled rather than created per shot: a flash lives ~55 ms, so allocating a
 * Sprite and a material for each one would churn GC during sustained fire.
 */
export interface MuzzleFlashPool {
  /** The group to add to the scene. */
  object: THREE.Object3D;
  /**
   * Show one flash at a world position, oriented toward `direction`.
   *
   * NOTE the anchor: these sprites are NOT canvas-centred (measured alpha
   * centroid x ~0.35-0.45), so the quad is pushed forward by half its width
   * along `direction` — centring it on the barrel tip would float every flash
   * out in front of the muzzle.
   */
  flash(position: THREE.Vector3, direction: THREE.Vector3): void;
  update(dt: number): void;
  dispose(): void;
}

export function createMuzzleFlashPool(size = 4, scale = 0.42): MuzzleFlashPool {
  const group = new THREE.Group();
  group.name = 'muzzle-flashes';
  const textures = MUZZLE_FLASHES.map((u) => {
    const t = textureLoader.load(u);
    t.colorSpace = THREE.SRGBColorSpace;
    // Pixel art — keep the hard pixel edges instead of smearing them.
    t.magFilter = THREE.NearestFilter;
    t.minFilter = THREE.NearestFilter;
    return t;
  });

  interface Slot { sprite: THREE.Sprite; remaining: number }
  const slots: Slot[] = [];
  for (let i = 0; i < size; i++) {
    const mat = new THREE.SpriteMaterial({
      map: textures[0]!,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      fog: false,
    });
    const s = new THREE.Sprite(mat);
    s.visible = false;
    s.scale.set(scale, scale, 1);
    group.add(s);
    slots.push({ sprite: s, remaining: 0 });
  }

  let next = 0;
  let pick = 0;
  return {
    object: group,
    flash(position, direction) {
      const slot = slots[next % slots.length]!;
      next++;
      // Cycle rather than Math.random(): deterministic for tests, and the
      // per-shot variation the pack asks for is still there. (A global
      // Math.random() spy also corrupts THREE's UUID generation — see the
      // idle-variety post-mortem in character-animations.ts.)
      pick = (pick + 1) % textures.length;
      const mat = slot.sprite.material as THREE.SpriteMaterial;
      mat.map = textures[pick]!;
      mat.needsUpdate = true;
      const dir = direction.clone().normalize();
      slot.sprite.position.copy(position).addScaledVector(dir, scale * 0.5);
      slot.sprite.visible = true;
      slot.remaining = MUZZLE_FLASH_SECONDS;
    },
    update(dt) {
      for (const slot of slots) {
        if (slot.remaining <= 0) continue;
        slot.remaining -= dt;
        if (slot.remaining <= 0) slot.sprite.visible = false;
      }
    },
    dispose() {
      for (const slot of slots) (slot.sprite.material as THREE.SpriteMaterial).dispose();
      for (const t of textures) t.dispose();
      group.clear();
    },
  };
}

/**
 * ─── The model-keyed ambient VFX is GONE (2026-09-15) ───────────────────────
 * `AMBIENT_VFX_BY_MODEL` + `attachAmbientVfx` used to light every campfire and
 * fire barrel automatically, from the model URL, with nothing authored. Once
 * `ultra_map_editor` could place fire and smoke by hand, that became a second
 * uncontrollable source: the editor showed one campfire flame the author owned
 * and the game drew two, the extra one unselectable and untunable. Janeks:
 * *"the campfire and other elements already have their fire sprites that i can
 * adjust in map editor ... Remove old ones. Will handle manually from now on."*
 *
 * So fire and smoke now come from exactly one place — `WorldData.spriteSpawns`,
 * below. A newly placed campfire is dark until someone puts a flame on it,
 * which is the trade that was made deliberately.
 */

/**
 * A deterministic phase in [0, 1) from an id.
 *
 * Two fires started on the same frame flicker in perfect lockstep, which reads
 * as fake instantly. Derived from the id rather than `Math.random()` for the
 * reason `createMuzzleFlashPool` spells out: a global random spy in a test
 * corrupts THREE's own UUID generation, and a map is supposed to render
 * identically every time it is loaded.
 *
 * FNV-1a WITH AN AVALANCHE STEP, not the `h * 31 + c` this started as. The
 * simple version is order-sensitive but not well mixed: `"fire-1"` and
 * `"fire-2"` hash one apart, so their phases landed 0.001 apart and both
 * sprites opened on the SAME sheet frame — the exact lockstep this exists to
 * break, and invisible unless you compare two fires side by side. Ids on a map
 * differ by one character constantly (`fire-1`, `fire-2`, …), so that is the
 * common case here, not a corner one.
 */
export function phaseFromId(id: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Final mix, so a one-bit input change spreads across the whole word.
  h ^= h >>> 15;
  h = Math.imul(h, 0x2545f491);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

export interface SpriteSpawnField {
  /** One group holding every authored billboard — added to the scene as a unit. */
  object: THREE.Group;
  /**
   * Each billboard keyed by its `SpriteSpawn.id`.
   *
   * Exposed for the same reason `SceneHandles.objectMeshes` is: `ultra_map_editor`
   * attaches its transform gizmo to one of these and tweaks opacity/colour/size
   * on the live material while a slider is dragged, instead of rebuilding the
   * whole field on every tick (a rebuild re-clones the sheet texture, and it
   * also restarts the animation, so the flame visibly stutters).
   */
  sprites: Map<string, THREE.Sprite>;
  update(dt: number): void;
  dispose(): void;
}

/**
 * The world-space Y a spawn's billboard is CENTRED at.
 *
 * `SpriteSpawn.positionY` is the BOTTOM of the card, not its middle — see that
 * type's doc comment for why. A `THREE.Sprite`'s own `position` is its centre,
 * so the two differ by half the height, and getting this backwards buries half
 * of every fire in the ground at the default `positionY` of 0.
 *
 * Pure and exported so the editor's gizmo can invert it without duplicating
 * the convention (a drag gives a centre; the authored field wants a base).
 */
export function spawnCenterY(spawn: Pick<SpriteSpawn, 'positionY' | 'height'>): number {
  return (spawn.positionY ?? 0) + spawn.height / 2;
}

/**
 * Hand-placed sprite VFX — `WorldData.spriteSpawns`, authored in
 * `ultra_map_editor` and rendered by the live game from the same call.
 *
 * The only source of fire and smoke in the game since the model-keyed
 * auto-attach was deleted (see the note above). It places exactly what was
 * authored and nothing else — which is what makes it possible to put a fire
 * where no burning prop stands, and equally what makes a fresh campfire dark
 * until someone lights it.
 */
export function buildSpriteSpawns(spawns: readonly SpriteSpawn[] | undefined): SpriteSpawnField {
  const group = new THREE.Group();
  group.name = 'sprite-spawns';
  const sprites = new Map<string, THREE.Sprite>();
  const animated: AnimatedSprite[] = [];

  for (const spawn of spawns ?? []) {
    const def = SPRITE_SHEETS[spawn.sheet];
    // Map data reaches the editor as plain JSON over its dev-server endpoint,
    // so `sheet` is only as narrow as whatever is on disk. An unknown sheet
    // draws nothing rather than throwing: one bad entry must not take the
    // whole map down.
    if (!def) continue;
    const s = createAnimatedSprite(def, {
      width: spawn.width,
      height: spawn.height,
      opacity: spawn.opacity,
      color: spawn.color,
      // Over one full loop of THIS sheet, so the offsets are spread evenly
      // across its frames rather than bunched by an arbitrary constant.
      phase: phaseFromId(spawn.id) * (def.frames / def.fps),
    });
    s.object.position.set(spawn.position.x, spawnCenterY(spawn), spawn.position.z);
    s.object.name = `sprite-spawn:${spawn.id}`;
    s.object.userData.spriteSpawnId = spawn.id;
    group.add(s.object);
    sprites.set(spawn.id, s.object);
    animated.push(s);
  }

  return {
    object: group,
    sprites,
    update(dt) {
      for (const s of animated) s.update(dt);
    },
    dispose() {
      for (const s of animated) {
        s.object.removeFromParent();
        s.dispose();
      }
      animated.length = 0;
      sprites.clear();
      group.removeFromParent();
    },
  };
}
